/**
 * @file Consultation audio settings — the five values the Advanced Settings dialog controls,
 *       and the two shapes the consult pages feed them into.
 * @module utils/consult/audioSettings
 *
 * S23 of ClickUp 86d3zmpwv.
 *
 * ═══ WHAT REACHES WHAT, AND WHEN ══════════════════════════════════════════════════════════
 * `voiceThreshold` / `silenceTimeout` tune voice-activity detection and are applied LIVE: the
 * page hands them to `AudioStreamingService.setVadOptions()`, whose monitor loop re-reads both
 * fields once per animation frame.
 *
 * `echoCancellation` / `noiseSuppression` / `autoGainControl` are getUserMedia constraints and
 * reach the NEXT microphone acquisition only. A capture already running keeps the constraints
 * its track was acquired with — the consult pages cache the stream in `audioStreamRef` and
 * re-acquire only when it is missing, and re-acquiring mid-consult would drop the audio the
 * transcript is being built from. That is a deliberate limit, not an oversight: do not
 * describe these three as live, and do not "fix" it by stopping a live track.
 *
 * ═══ DEFAULTS MIRROR THE SERVICE, NOT THE DIALOG ══════════════════════════════════════════
 * `AudioStreamingService` defaults to voiceThreshold 2 / silenceTimeout 400 and, before S23,
 * no consult page passed either — so 2/400 is what runs in the field and the defaults here
 * keep it. `AdvancedSettingsDialog` carries its own `voiceThreshold = 4` default; the pages
 * now pass these values in explicitly, so the dialog opens showing what the service is
 * actually running rather than a number nothing was using.
 */

/** The audio preferences a consult page holds. Exactly the fields something in the audio path reads. */
export interface ConsultAudioSettings {
	/** VAD trip level against the analyser's mean deviation from silence. The dialog offers 1–10. */
	voiceThreshold: number;
	/** Milliseconds of silence after speech before the chunk is flushed to talk. */
	silenceTimeout: number;
	echoCancellation: boolean;
	noiseSuppression: boolean;
	autoGainControl: boolean;
}

export const DEFAULT_CONSULT_AUDIO_SETTINGS: ConsultAudioSettings = {
	voiceThreshold: 2,
	silenceTimeout: 400,
	echoCancellation: true,
	noiseSuppression: true,
	autoGainControl: true,
};

/**
 * The `audio` half of a getUserMedia constraint set.
 *
 * `deviceId` is pinned only when a microphone was actually chosen: `{ exact: "" }` is an
 * over-constrained request that fails with OverconstrainedError instead of falling back to the
 * system default, and `selectedMicrophone` is `""` until device enumeration resolves.
 */
export const consultAudioConstraints = (settings: ConsultAudioSettings, deviceId?: string): MediaTrackConstraints => ({
	deviceId: deviceId ? { exact: deviceId } : undefined,
	echoCancellation: settings.echoCancellation,
	noiseSuppression: settings.noiseSuppression,
	autoGainControl: settings.autoGainControl,
});

/**
 * Fold an Advanced Settings payload into the settings a page is holding.
 *
 * Names all five fields and never spreads. The dialog's Apply payload carries a sixth key,
 * `audioQuality`, that nothing in the audio path reads — spreading would park it in page state
 * where it reads as a wired setting, and would widen that state again every time the dialog
 * grows a control. Each field is type-checked because the payload arrives through an optional
 * callback prop, so a caller can hand over a partial object.
 */
export const mergeConsultAudioSettings = (current: ConsultAudioSettings, incoming?: Partial<ConsultAudioSettings>): ConsultAudioSettings => ({
	voiceThreshold: typeof incoming?.voiceThreshold === "number" ? incoming.voiceThreshold : current.voiceThreshold,
	silenceTimeout: typeof incoming?.silenceTimeout === "number" ? incoming.silenceTimeout : current.silenceTimeout,
	echoCancellation: typeof incoming?.echoCancellation === "boolean" ? incoming.echoCancellation : current.echoCancellation,
	noiseSuppression: typeof incoming?.noiseSuppression === "boolean" ? incoming.noiseSuppression : current.noiseSuppression,
	autoGainControl: typeof incoming?.autoGainControl === "boolean" ? incoming.autoGainControl : current.autoGainControl,
});
